import { Client, createClient, initialize, User, UserCredentials } from '@botpress/webchat-client'
import { createEventEmitter, EventEmitter } from '@bpinternal/shared'
import { nanoid } from 'nanoid'
import { useEffect, useMemo, useRef, useState } from 'react'
import { integrationMessageToBlockMessage } from '../adapters'
import { SOUND_EFFECTS } from '../assets/audio'
import { type Events } from '../events'
import { getUseWebchatClientStore, StorageLocation, useMessagesStore, useParticipantStore } from '../stores'
import { getUseChatSoundStore } from '../stores/soundStore'
import {
  INVALID_API_URL,
  isFileType,
  MISSING_CLIENT_ID,
  NETWORK_ERROR,
  NOT_A_PARTICIPANT,
  retryWithBackoff,
  type BlockMessage,
  type Feedback,
  type FileType,
  type IntegrationMessage,
  type WebchatError,
} from '../types'
import { mapToWebchatError } from '../utils'
import { isValidUrl } from '../utils/url'
import { useTimeoutState } from './useTimeoutState'

type UserResponse = Awaited<ReturnType<Client['getUser']>>['user']
type UserProfile = Omit<User, 'id' | 'createdAt' | 'updatedAt'>

type Props = {
  adminSecret?: string
  user?: UserCredentials
  conversationId?: string
  clientId: string
  botId?: string
  apiUrl?: string
  storageKey?: string
  storageLocation?: StorageLocation
  soundEnabled?: boolean
}

type ClientStates = 'connecting' | 'connected' | 'error' | 'disconnected'

type ScopedClient = {
  sendMessage: (payload: IntegrationMessage['payload']) => Promise<void>
  sendEvent: (event: Record<string, any>, options?: { bindConversation?: boolean; bindUser?: boolean }) => Promise<void>
  uploadFile: (file: File) => Promise<{ fileUrl: string; name: string; type: FileType }>
  addMessageFeedback: (messageId: string, feedback: Feedback) => Promise<void>
  getUser: () => Promise<UserResponse>
  updateUser: (user: UserProfile) => Promise<User>
}

export type UseWebchatReturn =
  | {
      clientState: 'connected'
      on: EventEmitter<Events>['on']
      client: ScopedClient
      messages: BlockMessage[]
      conversationId: string
      newConversation: () => Promise<void>
      user?: UserCredentials
      isTyping: boolean
      participants: User[]
      soundOn: boolean
      toggleSound: () => void
      disableSendButton: boolean
      error?: WebchatError
    }
  | {
      clientState: 'connecting' | 'error' | 'disconnected'
      on: EventEmitter<Events>['on']
      client: undefined
      messages: BlockMessage[]
      conversationId: undefined
      newConversation: () => Promise<void>
      user: undefined
      isTyping: undefined
      participants: User[]
      soundOn: boolean
      toggleSound: () => void
      disableSendButton: boolean
      error?: WebchatError
    }

export function useWebchat({
  apiUrl = 'https://webchat.botpress.cloud',
  clientId,
  storageKey,
  storageLocation,
  adminSecret,
  soundEnabled,
  ...props
}: Props): UseWebchatReturn {
  const useWebchatClientStore = getUseWebchatClientStore(storageKey, storageLocation)

  const storageConversationId = useWebchatClientStore((state) => state.conversationId)
  const storageUserCredentials = useWebchatClientStore((state) => state.user)
  const setStorageConversationId = useWebchatClientStore((state) => state.setConversationId)
  const setStorageUserCredentials = useWebchatClientStore((state) => state.setUser)
  const clearConversationId = useWebchatClientStore((state) => state.clearConversationId)

  const messages = useMessagesStore((state) => state.messages)
  const saveMessage = useMessagesStore((state) => state.saveMessage)
  const updateMessage = useMessagesStore((state) => state.updateMessage)
  const fetchMessages = useMessagesStore((state) => state.fetchMessages)
  const setMessages = useMessagesStore((state) => state.setMessages)
  const clearMessages = useMessagesStore((state) => state.clearMessages)

  const participants = useParticipantStore((state) => state.participants)
  const setParticipants = useParticipantStore((state) => state.setParticipants)
  const addParticipant = useParticipantStore((state) => state.addParticipant)
  const removeParticipant = useParticipantStore((state) => state.removeParticipant)
  const clearParticipants = useParticipantStore((state) => state.clearParticipants)

  const useChatSoundStore = getUseChatSoundStore(`webchat-sound-${clientId}`)
  const soundOn = useChatSoundStore((s) => s.soundOn)
  const toggleSound = useChatSoundStore((s) => s.toggleSound)

  const { state: isTyping, setState: setIsTyping } = useTimeoutState()
  const { state: disableSendButton, setState: setdisableSendButton } = useTimeoutState()
  const emitter = useMemo(() => createEventEmitter<Events>(), [])

  const [clientState, setClientState] = useState<ClientStates>('connecting')
  const [conversationId, setConversationId] = useState<string | undefined>(props.conversationId)
  const [userCredentials, setUserCredentials] = useState<UserCredentials | undefined>(props.user)
  const [httpClient, setHttpClient] = useState<Client | undefined>(undefined)
  const [initToggle, setInitToggle] = useState(false)
  const [error, setError] = useState<WebchatError | undefined>(undefined)

  const initializing = useRef(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const soundEnabledRef = useRef<boolean>(soundEnabled ?? false)
  useEffect(() => {
    soundEnabledRef.current = soundEnabled ?? false
  }, [soundEnabled])

  const soundOnRef = useRef<boolean>(soundOn)

  useEffect(() => {
    soundOnRef.current = soundOn
  }, [soundOn])

  useEffect(() => {
    const configError = checkConfiguration(clientId, apiUrl)
    if (configError) {
      setError(configError)
      setClientState('error')
      return
    }

    if (initializing.current) return
    initializing.current = true

    setIsTyping(false)
    setClientState('connecting')

    try {
      audioRef.current = new Audio()
      audioRef.current.preload = 'auto'
      audioRef.current.src = SOUND_EFFECTS.chatMessageNotification
      audioRef.current.volume = 0.9
    } catch (error) {
      console.error('Error loading sound effect:', error)
      audioRef.current = null
    }

    let initParams
    if (props.user?.userToken && props.conversationId) {
      initParams = {
        conversationId: props.conversationId,
        userToken: props.user?.userToken,
      }
    } else if (props.user?.userToken) {
      initParams = {
        conversationId: undefined,
        userToken: props.user?.userToken,
      }
    } else {
      initParams = {
        conversationId: storageConversationId,
        userToken: storageUserCredentials?.userToken,
      }
    }

    const on = initialize({
      apiUrl,
      clientId,
      userToken: initParams.userToken,
      conversationId: initParams.conversationId,
      adminSecret,
    })

    let unsubscribeFunctions: Array<() => void> = []

    const onInitUnsub = on('init', ({ conversation, messages, participants, user }) => {
      const client = createClient({
        clientId,
        apiUrl,
        userKey: user.userKey,
      })

      client
        .createEvent({
          conversationId: conversation.id,
          payload: {
            type: 'conversation_started',
            data: {},
          },
        })
        .then(({ event }) => {
          if ((event.payload.data as Record<string, Record<string, string>>)?.status?.isInitial) {
            emitter.emit('conversation', conversation.id)
          }
        })

      unsubscribeFunctions = [
        ...unsubscribeFunctions,
        on('error', (err) => {
          setError((prev) => prev || mapToWebchatError(err))
          setClientState('error')
          emitter.emit('error', new Error(`Connection to conversation lost: ${err?.message}`))
        }),
        on('message_created', (ev) => {
          const message = integrationMessageToBlockMessage(ev)

          if (user.id !== message.authorId) {
            if (soundEnabledRef.current && soundOnRef.current && audioRef.current) {
              audioRef.current.play().catch(() => {})
            }
            setIsTyping(false)
            setdisableSendButton(false)
            emitter.emit('isTyping', { isTyping: false, timeout: 0 })
          }

          saveMessage(message)
          emitter.emit('message', message)
        }),
        on('message_status_changed', (ev) => {
          if (ev.newStatus === 'processing') {
            updateMessage(ev.message.metadata?.clientMessageId, { status: ev.newStatus })
          }
        }),
        on('typing_stopped', () => {
          setIsTyping(false)
          setdisableSendButton(false)
        }),
        on('typing_started', () => {
          setIsTyping(true, 5000)
          setdisableSendButton(true, 5000)
          emitter.emit('isTyping', { isTyping: true, timeout: 5000 })
        }),
        on('participant_added', (ev) => {
          addParticipant(ev.participant)
          emitter.emit('participantAdded', ev.participant)
        }),
        on('participant_removed', (ev) => {
          removeParticipant(ev.participantId)
          emitter.emit('participantRemoved', ev.participantId)
        }),
        on('webchat_visibility', (ev) => emitter.emit('webchatVisibility', ev.visibility)),
        on('webchat_config', (ev) => emitter.emit('webchatConfig', ev.config)),
        on('custom', (ev) => emitter.emit('customEvent', ev.event)),
      ]

      setConversationId(conversation.id)
      setStorageConversationId(conversation.id)
      setUserCredentials({ userId: user.id, userToken: user.userKey })
      setStorageUserCredentials({ userId: user.id, userToken: user.userKey })

      setParticipants(participants)
      setMessages(
        messages
          .reverse()
          .map((message) => integrationMessageToBlockMessage(message))
          .filter(Boolean)
      )

      setError(undefined)
      setClientState('connected')
      setHttpClient(client)
    })

    unsubscribeFunctions.push(onInitUnsub)
    return () => {
      initializing.current = false
      unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
    }
  }, [props.conversationId, props.user?.userToken, initToggle])

  const fetchMessagesAndParticipants = async (client?: Client, convId?: string) => {
    const activeConversationId = convId || conversationId

    if (!client || !activeConversationId) return

    const messages = await fetchMessages({ client, conversationId: activeConversationId })
    setMessages(messages)
    const { participants } = await client.listParticipants({ conversationId: activeConversationId })
    setParticipants(participants)
  }

  const startNewConversation = async (client?: Client): Promise<void> => {
    if (!client) {
      throw new Error('Cannot start new conversation: client is not initialized')
    }
    clearConversationId()
    clearMessages()
    clearParticipants()

    setConversationId(undefined)
    setInitToggle((prev) => !prev)
  }

  async function handleWebchatError(webchatError: WebchatError, client: Client) {
    // Add here new error recovery strategies
    const errorRecoveryStrategies = [
      {
        predicate: (err: WebchatError) => err.message === NOT_A_PARTICIPANT || err.type === 'ResourceNotFound',
        recover: () => startNewConversation(client),
      },
      {
        predicate: (err: WebchatError) =>
          err.type === 'network' || err.type === 'disconnected' || err.message === NETWORK_ERROR,
        recover: () => fetchMessagesAndParticipants(client, conversationId),
      },
    ]

    const strategy = errorRecoveryStrategies.find((s) => s.predicate(webchatError))
    if (!strategy) {
      setClientState('error')
      setError(webchatError)
      return
    }

    try {
      await retryWithBackoff(strategy.recover, 3, 5000)
      setError(undefined)
      setClientState('connected')
    } catch (error) {
      setError(mapToWebchatError(error))
      setClientState('error')
    }
  }

  if (clientState !== 'connected' || !userCredentials || !conversationId || !httpClient) {
    return {
      clientState: clientState as 'connecting' | 'error' | 'disconnected',
      on: emitter.on,
      client: undefined,
      messages: [],
      conversationId: undefined,
      newConversation: async () => {
        await startNewConversation(httpClient)
      },
      user: undefined,
      isTyping: undefined,
      participants: [],
      toggleSound,
      soundOn,
      error,
      disableSendButton: disableSendButton,
    }
  }

  const client = {
    sendMessage: async (payload: IntegrationMessage['payload']) => {
      const temporaryId = nanoid()

      const message = {
        id: temporaryId,
        createdAt: new Date().toISOString(),
        userId: userCredentials.userId,
        conversationId,
        payload,
        metadata: {
          clientMessageId: temporaryId,
          delivered: false,
        },
      }

      const blockMessage = integrationMessageToBlockMessage(message)
      blockMessage.status = 'pending'
      saveMessage(blockMessage)

      setdisableSendButton(true, 5000)

      try {
        await httpClient.createMessage({
          conversationId,
          payload,
          metadata: {
            clientMessageId: temporaryId,
          },
        })
      } catch (error) {
        await handleWebchatError(mapToWebchatError(error), httpClient)
        emitter.emit('error', error as Error)
      }
    },
    sendEvent: async (event: Record<string, unknown>, options?: { bindConversation?: boolean; bindUser?: boolean }) => {
      await httpClient.createEvent({
        conversationId,
        payload: {
          type: 'custom',
          data: event,
        },
        bindConversation: options?.bindConversation ?? true,
        bindUser: options?.bindUser ?? true,
      })
    },
    uploadFile: async (file: File): Promise<{ fileUrl: string; name: string; type: FileType }> => {
      const { size, name } = file

      const getFileBuffer = (file: File): Promise<ArrayBuffer> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = (event) => resolve(event.target?.result as ArrayBuffer)
          reader.onerror = (event) => reject(event.target?.error)
          reader.readAsArrayBuffer(file)
        })
      }

      const buffer = await getFileBuffer(file)

      const thirtyDaysFromNow = new Date()
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)

      const {
        file: { uploadUrl, contentType: mimeType, url: fileUrl },
      } = await httpClient!.createFile({
        size,
        key: `${nanoid(6)}-${file.name}`,
        accessPolicies: ['public_content'],
        index: false,
        tags: {
          source: 'integration',
          integrationName: 'webchat',
          conversation: conversationId,
          user: userCredentials.userId,
        },
        expiresAt: thirtyDaysFromNow.toISOString(),
      })
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-amz-tagging': 'public=true',
          'content-type': mimeType,
        },
        body: buffer,
      })

      const contentType = mimeType.split('/').shift() ?? ''
      const fileType = isFileType(contentType) ? contentType : ('file' as const)
      return { fileUrl, name, type: fileType }
    },
    addMessageFeedback: async (messageId: string, feedback: Feedback) => {
      updateMessage(messageId, { feedback: feedback.value })

      await httpClient.addMessageFeedback({
        messageId,
        ...feedback,
      })
    },
    getUser: async () => {
      const { user } = await httpClient.getUser()
      return user
    },
    updateUser: async (user: UserProfile) => {
      const { user: updatedUser } = await httpClient.updateUser({
        name: user.name,
        pictureUrl: user.pictureUrl,
        userData: user.data,
      })

      return updatedUser
    },
  }

  return {
    clientState: 'connected',
    on: emitter.on,
    client,
    messages,
    conversationId,
    newConversation: async () => {
      await startNewConversation(httpClient)
    },
    user: userCredentials,
    isTyping,
    participants,
    error,
    soundOn,
    toggleSound,
    disableSendButton,
  }
}

const checkConfiguration = (clientId: string, apiUrl: string): WebchatError | undefined => {
  if (!clientId) {
    return { type: 'configuration', message: MISSING_CLIENT_ID }
  }
  if (!isValidUrl(apiUrl)) {
    return { type: 'configuration', message: INVALID_API_URL }
  }
}
